import { supabase } from "@/utils/supabase";
import { TariffMapper } from "@/adapters/Tariff.mapper";
import { zTariffPairRowDto } from "@/types/tariff/tariff-pair.dto";
import { TariffPairMapper } from "@/adapters/TariffPair.mapper";
import { toB2BError } from "@/utils/b2bErrors";
export class TariffService {
    async getTariffsWithParams(params) {
        try {
            let query = supabase()
                .from("Tariffs")
                .select("*", { count: "exact" })
                .eq("isDeleted", false)
                .eq("isActive", true)
                // FR-003: hide LATE-twins from generic pickers (only ADVANCE parents)
                .eq("isLateTwin", false)
                // FR-006: company tariffs never appear in shared pickers (contract v2.0 §8, BR-TRF-03).
                // Ships to PROD only together with the corp schema — the column does not exist there before.
                .is("corp_company_id", null);
            if (params.filters) {
                const search = params.filters.search?.trim();
                if (search) {
                    const ors = [`name.ilike.%${search}%`];
                    query = query.or(ors.join(","));
                }
            }
            if (params.sorters && Object.keys(params.sorters).length > 0) {
                Object.entries(params.sorters).forEach(([field, order]) => {
                    if (order === "ascend")
                        query = query.order(field, { ascending: true });
                    else if (order === "descend")
                        query = query.order(field, { ascending: false });
                });
            }
            else {
                query = query.order("name", { ascending: true });
            }
            const from = (params.page - 1) * params.pageSize;
            query = query.range(from, from + params.pageSize - 1);
            const { data, error, count } = await query;
            if (error)
                throw error;
            return {
                data: data.map(TariffMapper.toDomain),
                total: count || 0,
                page: params.page,
                pageSize: params.pageSize,
            };
        }
        catch (error) {
            console.error("Error in getTariffsWithParams:", error);
            throw error;
        }
    }
    async getTariffsByIds(ids) {
        // FR-003: do NOT filter isLateTwin here — historical orders may reference
        // a LATE-twin and must still resolve its row for detail rendering.
        const { data, error } = await supabase()
            .from("Tariffs")
            .select("*")
            .in("id", ids)
            .eq("isDeleted", false)
            .eq("isActive", true);
        if (error) {
            throw error;
        }
        return data.map(TariffMapper.toDomain);
    }
    // ============================================================
    // FR-003: pair-aware methods
    // ============================================================
    async getPairs(params) {
        try {
            let query = supabase()
                .from("v_tariff_pairs")
                .select("*", { count: "exact" })
                // Hide soft-deleted advance rows
                .or("is_deleted.is.null,is_deleted.eq.false");
            if (params.filters) {
                const { type, isActive, search } = params.filters;
                if (type)
                    query = query.eq("type", type);
                if (typeof isActive === "boolean")
                    query = query.eq("is_active", isActive);
                const trimmed = search?.trim();
                if (trimmed) {
                    query = query.ilike("name", `%${trimmed}%`);
                }
            }
            if (params.sorters && Object.keys(params.sorters).length > 0) {
                Object.entries(params.sorters).forEach(([field, order]) => {
                    if (order === "ascend")
                        query = query.order(field, { ascending: true });
                    else if (order === "descend")
                        query = query.order(field, { ascending: false });
                });
            }
            else {
                query = query.order("name", { ascending: true });
            }
            const from = (params.page - 1) * params.pageSize;
            query = query.range(from, from + params.pageSize - 1);
            const { data, error, count } = await query;
            if (error)
                throw error;
            const rows = (data ?? []).map((r) => zTariffPairRowDto.parse(r));
            return {
                data: rows.map(TariffPairMapper.rowToDomain),
                total: count ?? 0,
                page: params.page,
                pageSize: params.pageSize,
            };
        }
        catch (error) {
            console.error("Error in getPairs:", error);
            throw error;
        }
    }
    async getPair(advanceId) {
        const { data, error } = await supabase()
            .from("v_tariff_pairs")
            .select("*")
            .eq("id_advance", advanceId)
            .single();
        if (error)
            throw error;
        return TariffPairMapper.rowToDomain(zTariffPairRowDto.parse(data));
    }
    async upsertPair(pair) {
        const { data, error } = await supabase().rpc("upsert_tariff_pair", {
            p_payload: pair,
        });
        if (error)
            throw error;
        if (!data?.success)
            throw new Error(data?.error ?? "upsert_tariff_pair failed");
        return {
            idAdvance: data.idAdvance,
            idLateTariff: data.idLateTariff ?? null,
        };
    }
    async softDeletePair(advanceId) {
        // BD trigger trg_tariff_cascade_soft_delete will mirror isDeleted to LATE-twin.
        const { error } = await supabase()
            .from("Tariffs")
            .update({ isDeleted: true, updatedAt: new Date().toISOString() })
            .eq("id", advanceId)
            .eq("isLateTwin", false);
        if (error)
            throw error;
    }
    async previewCost(args) {
        const payload = {
            scheduledDate: args.scheduledDate,
        };
        if (args.idTariff)
            payload.idTariff = args.idTariff;
        if (args.chargeLevel !== undefined)
            payload.chargeLevel = args.chargeLevel;
        if (args.batterySize !== undefined)
            payload.batterySize = args.batterySize;
        if (args.vehicleId || args.modelId) {
            payload.auto = {
                ...(args.vehicleId ? { vehicleId: args.vehicleId } : {}),
                ...(args.modelId ? { modelId: args.modelId } : {}),
            };
        }
        if (args.idSubscription)
            payload.idSubscription = args.idSubscription;
        if (args.idCompany)
            payload.idCompany = args.idCompany;
        // FR-007 (WP-DSP-4 §3.3): тариф мок-компании — ответ мока; реальная компания уходит на сервер.
        // Литерал, а не featureFlags: иначе webpack не выбросит ветку из боевой сборки (G4).
        if (process.env.REACT_APP_B2B_MOCK === "true" && args.idCompany) {
            const { mockCompanyPreview } = await import("./mock/corpFixtures");
            const mockPreview = mockCompanyPreview(args.idCompany, args.scheduledDate);
            if (mockPreview)
                return mockPreview;
        }
        const { data, error } = await supabase().rpc("calculate_price", payload);
        if (error)
            throw toB2BError(error);
        // FR-007: legacy-RPC отвечает конвертом {success, error, error_code} (контракт §1.7),
        // символ отказа лежит в error_code — toB2BError его понимает.
        if (!data?.success)
            throw toB2BError(data);
        return data;
    }
}
export const tariffService = new TariffService();
